import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useUIStore } from '../stores/ui/uiStore';
import { useDataStore } from '../stores/data/dataStore';
import { useToasterStore } from '../stores/ui/toasterStore';
import GmgnChart from './GmgnChart';
import RankChart from './RankChart';
import PnLDistributionChart from './PnLDistributionChart';
import UnrealizedPnLDistributionChart from './UnrealizedPnLDistributionChart';
import BuySellWallChart from './BuySellWallChart';
import TokenDetailHeader from './TokenDetailHeader';
import { MODAL_WIDTH } from '../const';
import { api } from '../api';
import { formatNumber } from '../utils/formatters';
import AFList from './AFList';
import TokenAlertList from './TokenAlertList';
import useEscapeKeyClose from '../hooks/useEscapeKeyClose';
import getApiErrorMessage from '../utils/getApiErrorMessage';
import { formatTimestampToLocaleString } from '../utils/time';
import AdminInfoTab from './AdminInfoTab';
import { feManagementSystemApi } from '../api/fe-management-system-api';

interface TokenDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TokenDetailModal: React.FC<TokenDetailModalProps> = ({ isOpen, onClose }) => {
  const { selectedToken, openCreateNoteModal } = useUIStore();
  const getTokenData = useDataStore((state) => state.getTokenData);
  const { showToaster } = useToasterStore();
  const [rankTimelineData, setRankTimelineData] = useState<any[]>([]);
  const [isLoadingRankData, setIsLoadingRankData] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Buy/Sell Wall Chart 데이터
  const [buySellWallChartData, setBuySellWallChartData] = useState<any>(null);

  // AFList 관련 데이터
  const [alphaFinderMetrics, setAlphaFinderMetrics] = useState<any>(null);
  const [evaluationResults, setEvaluationResults] = useState<any>(null);
  const [afDetails, setAfDetails] = useState<any[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [communityId, setCommunityId] = useState<string>('');
  const [hasAdminInfo, setHasAdminInfo] = useState(false);
  const [isLoadingDexScreenerSync, setIsLoadingDexScreenerSync] = useState(false);

  // Token Investigations 분석 결과
  const [investigationAnalysis, setInvestigationAnalysis] = useState<{
    totalUniqueAddresses: number;
    fixedLabelGroups: Record<string, {
      uniqueAddresses: number;
      currentTokenAmountInPctSum: number;
      maxTokenAmountInPctSum: number;
      weightedAvgBuyMC: number;
    }>;
    top10CurrentPctSum: number;
    top10WeightedAvgBuyMC: number;
  } | null>(null);

  // tokenData를 먼저 가져와서 hook에서 사용 (조건부 hook 호출 방지)
  const tokenData = selectedToken ? (getTokenData(selectedToken.tokenAddress) ?? null) : null;

  // Token Investigations 데이터 분석 함수
  const analyzeTokenInvestigations = useCallback((data: any[]) => {
    if (!data || data.length === 0) {
      setInvestigationAnalysis(null);
      return;
    }

    const validData = data.filter(item => item.firstTransactionTime != null && item.firstTransactionTime !== undefined);
    const uniqueAddresses = new Set(validData.map(item => item.address));
    const totalUniqueAddresses = uniqueAddresses.size;

    const fixedLabelGroups: Record<string, {
      uniqueAddresses: Set<string>;
      currentTokenAmountInPctSum: number;
      maxTokenAmountInPctSum: number;
      weightedAvgBuyMCSum: number;
      weightedAvgBuyMCWeight: number;
    }> = {};

    validData.forEach(item => {
      if (item.fixedLabels && Array.isArray(item.fixedLabels) && item.fixedLabels.length > 0) {
        item.fixedLabels.forEach((label: string) => {
          if (!fixedLabelGroups[label]) {
            fixedLabelGroups[label] = {
              uniqueAddresses: new Set(),
              currentTokenAmountInPctSum: 0,
              maxTokenAmountInPctSum: 0,
              weightedAvgBuyMCSum: 0,
              weightedAvgBuyMCWeight: 0,
            };
          }

          fixedLabelGroups[label].uniqueAddresses.add(item.address);
          fixedLabelGroups[label].currentTokenAmountInPctSum += (item.currentTokenAmountInPct || 0);
          fixedLabelGroups[label].maxTokenAmountInPctSum += (item.maxTokenAmountInPct || 0);

          const weight = item.currentTokenAmountInPct || 0;
          const avgBuyMC = item.avgBuyMC || 0;

          if (weight > 0 && avgBuyMC > 0) {
            fixedLabelGroups[label].weightedAvgBuyMCSum += avgBuyMC * weight;
            fixedLabelGroups[label].weightedAvgBuyMCWeight += weight;
          }
        });
      }
    });

    const sortedByCurrentPct = [...validData].sort((a, b) =>
      (b.currentTokenAmountInPct || 0) - (a.currentTokenAmountInPct || 0)
    );
    const top10ByCurrentPct = sortedByCurrentPct.slice(0, 10);
    const top10CurrentPctSum = top10ByCurrentPct.reduce((sum, item) => sum + (item.currentTokenAmountInPct || 0), 0);

    let weightedAvgBuyMC = 0;
    let totalWeight = 0;

    top10ByCurrentPct.forEach(item => {
      const weight = item.currentTokenAmountInPct || 0;
      const avgBuyMC = item.avgBuyMC || 0;
      if (weight > 0 && avgBuyMC > 0) {
        weightedAvgBuyMC += avgBuyMC * weight;
        totalWeight += weight;
      }
    });

    const finalWeightedAvgBuyMC = totalWeight > 0 ? weightedAvgBuyMC / totalWeight : 0;

    const processedFixedLabelGroups: Record<string, {
      uniqueAddresses: number;
      currentTokenAmountInPctSum: number;
      maxTokenAmountInPctSum: number;
      weightedAvgBuyMC: number;
    }> = {};

    Object.entries(fixedLabelGroups).forEach(([label, stats]) => {
      const weightedAvgBuyMC = stats.weightedAvgBuyMCWeight > 0
        ? stats.weightedAvgBuyMCSum / stats.weightedAvgBuyMCWeight
        : 0;

      processedFixedLabelGroups[label] = {
        uniqueAddresses: stats.uniqueAddresses.size,
        currentTokenAmountInPctSum: stats.currentTokenAmountInPctSum,
        maxTokenAmountInPctSum: stats.maxTokenAmountInPctSum,
        weightedAvgBuyMC,
      };
    });

    setInvestigationAnalysis({
      totalUniqueAddresses,
      fixedLabelGroups: processedFixedLabelGroups,
      top10CurrentPctSum,
      top10WeightedAvgBuyMC: finalWeightedAvgBuyMC,
    });
  }, []);

  // 통합 데이터 새로고침 함수
  const fetchAllData = useCallback(async (signal?: AbortSignal) => {
    if (!tokenData) return;

    setIsRefreshing(true);
    setIsLoadingRankData(true);

    try {
      // 0. FE Management System Token State (Note & Community ID)
      try {
        const feState = await feManagementSystemApi.getTokenState(tokenData.tokenAddress);
        if (!signal?.aborted) {
          const commId = feState?.note?.twitterCommunityId || '';
          setCommunityId(commId);
          setHasAdminInfo(!!commId);
        }
      } catch (error) {
        console.error('FE Token State 조회 실패:', error);
      }

      // 1. Rank Timeline 데이터
      try {
        const rankData = await api.getRankTimeline(tokenData.tokenAddress, 5000, signal);
        if (!signal?.aborted) {
          setRankTimelineData(Array.isArray(rankData) ? rankData : []);
        }
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.error('랭킹 타임라인 데이터 조회 실패:', error);
          if (!signal?.aborted) {
            setRankTimelineData([]);
          }
        }
      } finally {
        if (!signal?.aborted) {
          setIsLoadingRankData(false);
        }
      }

      // 2. Buy/Sell Wall Chart 데이터
      try {
        const chartData = await api.getBuySellWallChart(tokenData.tokenAddress);
        if (!signal?.aborted) {
          setBuySellWallChartData(chartData);
        }
      } catch (error) {
        console.error('Buy/Sell Wall Chart 데이터 조회 실패:', error);
        if (!signal?.aborted) {
          setBuySellWallChartData(null);
        }
      }

      // 3. AFList 관련 데이터
      try {
        const metricsResult = await api.getTokenAlphaFinderMetrics(tokenData.tokenAddress);
        if (!signal?.aborted) {
          setAlphaFinderMetrics(metricsResult.data);
        }
      } catch (error) {
        console.error('❌ getTokenAlphaFinderMetrics 에러:', error);
      }

      try {
        const afsWithEvaluationResult = await api.getTokenAfsWithEvaluation(tokenData.tokenAddress);
        if (!signal?.aborted) {
          if (afsWithEvaluationResult?.afDetails) {
            setAfDetails(afsWithEvaluationResult.afDetails);
          }

          const transformedResults: any = { results: {} };
          if (afsWithEvaluationResult?.afDetails) {
            afsWithEvaluationResult.afDetails.forEach((afDetail: any) => {
              if (afDetail.address && afDetail.evaluateResult) {
                const transformedEvaluateResult = {
                  ...afDetail.evaluateResult,
                  metrics: {
                    ...afDetail.evaluateResult.metrics,
                    avg_holding_time_in_min: afDetail.evaluateResult.metrics?.avgHoldingTimeInMin,
                    win_rate: afDetail.evaluateResult.metrics?.winRate,
                    scalping_cnt: afDetail.evaluateResult.metrics?.scalpingCnt,
                    total_invest_cnt: afDetail.evaluateResult.metrics?.totalInvestCnt,
                    mc_range_metrics: (() => {
                      if (afDetail.evaluateResult.metrics?.bettingRangeMetrics) {
                        return afDetail.evaluateResult.metrics.bettingRangeMetrics.map((bettingMetric: any) => ({
                          range: bettingMetric.range,
                          count: bettingMetric.count,
                          roi: 0,
                        }));
                      }
                      return [];
                    })(),
                  },
                };
                transformedResults.results[afDetail.address] = transformedEvaluateResult;
              }
            });
          }
          setEvaluationResults(transformedResults);
        }
      } catch (error) {
        console.error('❌ getTokenAfsWithEvaluation 에러:', error);
      }

      // 4. Token Investigations 데이터
      try {
        const investigationsData = await api.getTokenInvestigations(tokenData.tokenAddress, undefined, signal);
        if (!signal?.aborted && investigationsData) {
          analyzeTokenInvestigations(investigationsData);
        }
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          console.error('❌ getTokenInvestigations 에러:', error);
        }
      }
    } catch (error) {
      console.error('❌ 통합 데이터 새로고침 중 에러:', error);
    } finally {
      if (!signal?.aborted) {
        setIsRefreshing(false);
      }
    }
  }, [tokenData, analyzeTokenInvestigations]);

  const handleDexScreenerSync = useCallback(async () => {
    if (!selectedToken || isLoadingDexScreenerSync) return;

    try {
      setIsLoadingDexScreenerSync(true);
      await feManagementSystemApi.refreshTokenNoteFromDexScreener(selectedToken.tokenAddress);
      showToaster('Data fetched from DexScreener successfully.', 'success');
      fetchAllData();
    } catch (error: any) {
      console.error('DexScreener 동기화 실패:', error);
      showToaster(getApiErrorMessage(error, 'DexScreener sync failed.'), 'error');
    } finally {
      setIsLoadingDexScreenerSync(false);
    }
  }, [selectedToken, isLoadingDexScreenerSync, fetchAllData, showToaster]);

  useEffect(() => {
    if (!tokenData) {
      setRankTimelineData([]);
      setBuySellWallChartData(null);
      setAlphaFinderMetrics(null);
      setEvaluationResults(null);
      setInvestigationAnalysis(null);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    fetchAllData(signal);

    const interval = setInterval(() => {
      if (!signal.aborted) {
        fetchAllData(signal);
      }
    }, 30000);

    return () => {
      clearInterval(interval);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [tokenData, fetchAllData]);

  const cumulativeRankData = useMemo(() => {
    if (!rankTimelineData || rankTimelineData.length === 0) return [];
    return rankTimelineData.map(item => ({
      timestamp: new Date(item.timestamp).getTime(),
      percentage: item.cumulativeConvictionAtSameTimeRank * 100
    }));
  }, [rankTimelineData]);

  const unifiedTimeRange = useMemo(() => {
    if (!rankTimelineData || rankTimelineData.length === 0) return null;
    const rankTimeRange = {
      start: Math.min(...rankTimelineData.map(item => new Date(item.timestamp).getTime())),
      end: Math.max(...rankTimelineData.map(item => new Date(item.timestamp).getTime()))
    };
    const timePadding = (rankTimeRange.end - rankTimeRange.start) * 0.1;
    return {
      start: rankTimeRange.start - timePadding,
      end: rankTimeRange.end + timePadding
    };
  }, [rankTimelineData]);

  useEscapeKeyClose(isOpen, onClose);

  const handleStartInvestigationSync = useCallback(async () => {
    if (!selectedToken) return;
    try {
      await api.startTokenInvestigationSync(selectedToken.tokenAddress);
      showToaster('Token research sync started successfully!', 'success');
    } catch (error: any) {
      console.error('토큰 조사 싱크 시작 실패:', error);
      showToaster(getApiErrorMessage(error, 'Failed to start token research sync.'), 'error');
    }
  }, [selectedToken, showToaster]);

  if (!isOpen || !selectedToken) return null;

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={handleBackdropClick}>
      <div className="absolute inset-0 bg-black bg-opacity-30" onClick={onClose} />
      <div
        className="relative h-full bg-gray-900 border-l border-gray-700 shadow-2xl flex flex-col"
        style={{ width: `${MODAL_WIDTH}px`, boxShadow: '-16px 0px 50px 20px rgba(0, 0, 0, 0.5)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <TokenDetailHeader
          tokenAddress={selectedToken?.tokenAddress || ''}
          tokenData={tokenData}
          onClose={onClose}
          onStartInvestigationSync={handleStartInvestigationSync}
        />
        <div className="p-4 overflow-y-auto flex-1 min-h-0">
          {tokenData ? (
            <div className="space-y-4">
              <div className="flex gap-4">
                <div style={{ width: MODAL_WIDTH / 2.2 - 24, flexShrink: 0 }} className="flex flex-col gap-4">
                  <div className="">
                    {hasAdminInfo ? (
                      <div className="">
                        <div className="max-h-[880px] overflow-y-auto">
                          <AdminInfoTab 
                            tokenAddress={selectedToken.tokenAddress} 
                            communityId={communityId} 
                            defaultSort="maxMc"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center p-8 bg-gray-800/30 rounded-xl border border-dashed border-gray-700">
                        <p className="text-gray-400 text-sm mb-4">Admin Info has not been created yet.</p>
                        <button
                          onClick={() => {
                            openCreateNoteModal({
                              tokenSymbol: tokenData.tokenInfo?.tokenSymbol || 'Unknown',
                              tokenAddress: tokenData.tokenAddress,
                              tokenName: tokenData.tokenInfo?.tokenName,
                              tokenTwitter: tokenData.tokenInfo?.twitter,
                              tokenWebsite: tokenData.tokenInfo?.website,
                              tokenCategory: tokenData.feTokenState?.category,
                            });
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors shadow-lg"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                          </svg>
                          Create Note & Sync Twitter
                        </button>
                        <button
                          onClick={handleDexScreenerSync}
                          disabled={isLoadingDexScreenerSync}
                          className=" mt-3 flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm font-semibold rounded-lg transition-colors border border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed justify-center"
                        >
                          {isLoadingDexScreenerSync ? (
                            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                            </svg>
                          )}
                          Sync from DexScreener
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-1 flex flex-col gap-4">
                  <GmgnChart
                    chain="sol"
                    tokenAddress={selectedToken.tokenAddress}
                    tokenSymbol={selectedToken.symbol}
                    theme="dark"
                    interval="15"
                    width="100%"
                    height="600px"
                    className="w-full z-50"
                  />
                  {!isLoadingRankData && cumulativeRankData.length > 0 && (
                    <div className="bg-gray-800/20 rounded-xl border border-gray-800 p-2">
                      <RankChart
                        data={cumulativeRankData}
                        width={MODAL_WIDTH - (MODAL_WIDTH / 2.2) - 40}
                        height={250}
                        title="Rank"
                        className="w-full"
                        timeRange={unifiedTimeRange}
                      />
                    </div>
                  )}
                  {isLoadingRankData && (
                    <div className="bg-gray-800/20 rounded-xl border border-gray-800 p-2 flex items-center justify-center" style={{ height: 250 }}>
                      <p className="text-gray-400 text-xs">랭킹 데이터를 불러오는 중...</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-white">AF Wallet List</h3>
                  <button
                    onClick={() => {
                      if (abortControllerRef.current) abortControllerRef.current.abort();
                      abortControllerRef.current = new AbortController();
                      fetchAllData(abortControllerRef.current.signal);
                    }}
                    disabled={isRefreshing}
                    className="p-1 hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="데이터 새로고침"
                  >
                    <svg className={`w-5 h-5 text-gray-400 hover:text-white transition-colors ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
                <AFList
                  addresses={afDetails.map(af => af.address)}
                  afDetails={afDetails}
                  addressTokenAmounts={afDetails.map(af => ({ address: af.address, tokenAmount: af.currentTokenHoldingAmount || 0 }))}
                  title=""
                  showCount={true}
                  maxHeight="max-h-100"
                  tokenAddress={selectedToken?.tokenAddress}
                  alphaFinderMetrics={alphaFinderMetrics}
                  evaluationResults={evaluationResults}
                  isLoading={isRefreshing}
                />
              </div>
              {investigationAnalysis && (
                <div className="px-4" style={{ marginBottom: -10 }}>
                  <div className="flex flex-col gap-6 w-full justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-white mb-3">Token Investigations</h3>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">Total Wallet Count:</span>
                          <span className="text-white font-semibold">{investigationAnalysis.totalUniqueAddresses}</span>
                        </div>
                        <span className="text-gray-600">|</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">Top 10 Pct Total:</span>
                          <span className="text-white font-semibold">{(investigationAnalysis.top10CurrentPctSum * 100).toFixed(2)}%</span>
                        </div>
                        <span className="text-gray-600">|</span>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">Top 10 Avg MC:</span>
                          <span className="text-white font-semibold">${formatNumber(investigationAnalysis.top10WeightedAvgBuyMC)}</span>
                        </div>
                      </div>
                    </div>
                    {Object.keys(investigationAnalysis.fixedLabelGroups).length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-white mb-3">Fixed Label Statistics</h3>
                        <div className="space-y-1.5">
                          {Object.entries(investigationAnalysis.fixedLabelGroups).map(([label, stats]) => (
                            <div key={label} className="flex items-center gap-4 text-sm">
                              <div className="flex items-center gap-1 w-40"><span className="text-yellow-400 font-semibold">[{label}]</span></div>
                              <div className="flex items-center gap-1 w-24"><span className="text-gray-400">Wallets:</span><span className="text-white font-semibold w-8 text-right">{stats.uniqueAddresses}</span></div>
                              <span className="text-gray-600">|</span>
                              <div className="flex items-center gap-1 w-42"><span className="text-gray-400">Current Pct:</span><span className="text-white font-semibold w-16 text-right">{(stats.currentTokenAmountInPctSum * 100).toFixed(2)}%</span></div>
                              <span className="text-gray-600">|</span>
                              <div className="flex items-center gap-1"><span className="text-gray-400">Max Pct:</span><span className="text-white font-semibold w-16 text-right">{(stats.maxTokenAmountInPctSum * 100).toFixed(2)}%</span></div>
                              <span className="text-gray-600">|</span>
                              <div className="flex items-center gap-1"><span className="text-gray-400">AF Avg MC:</span><span className="text-white font-semibold w-16 text-right">${formatNumber(stats.weightedAvgBuyMC)}</span></div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="w-[600px]">
                <TokenAlertList tokenAddress={selectedToken.tokenAddress} maxHeight="max-h-[200px]" />
              </div>
              {buySellWallChartData && (
                <div className="flex flex-col gap-4">
                  {buySellWallChartData?.pnlRangeChart?.plotlyConfig && (
                    <div className=" rounded-lg p-3">
                      <PnLDistributionChart
                        plotlyData={buySellWallChartData.pnlRangeChart.plotlyConfig.data}
                        plotlyLayout={buySellWallChartData.pnlRangeChart.plotlyConfig.layout}
                        width={MODAL_WIDTH - 80} height={300} className="w-full"
                      />
                    </div>
                  )}
                  {buySellWallChartData?.unrealizedPnlRangeChart?.plotlyConfig && (
                    <div className=" rounded-lg p-3">
                      <UnrealizedPnLDistributionChart
                        plotlyData={buySellWallChartData.unrealizedPnlRangeChart.plotlyConfig.data}
                        plotlyLayout={buySellWallChartData.unrealizedPnlRangeChart.plotlyConfig.layout}
                        width={MODAL_WIDTH - 80} height={300} className="w-full"
                      />
                    </div>
                  )}
                  {buySellWallChartData?.priceRangeChart?.plotlyConfig && (
                    <div className="ounded-lg p-3">
                      <BuySellWallChart
                        plotlyData={buySellWallChartData.priceRangeChart.plotlyConfig.data}
                        plotlyLayout={buySellWallChartData.priceRangeChart.plotlyConfig.layout}
                        width={MODAL_WIDTH - 80} height={300} className="w-full"
                      />
                    </div>
                  )}
                </div>
              )}
              {tokenData.devCluster.buyerCount > 0 && (
                <div className="bg-gray-800 rounded-lg p-3">
                  <h3 className="text-xs font-semibold text-white mb-2">개발자 클러스터</h3>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between"><span className="text-gray-400">현재 구매자 수:</span><span className="text-white">{tokenData.devCluster.buyerCount}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">이전 구매자 수:</span><span className="text-white">{tokenData.devCluster.previousBuyerCount}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">구매자 수 변화:</span><span className={`text-white ${tokenData.devCluster.buyerCountChange > 0 ? 'text-green-400' : tokenData.devCluster.buyerCountChange < 0 ? 'text-red-400' : ''}`}>{tokenData.devCluster.buyerCountChange > 0 ? '+' : ''}{tokenData.devCluster.buyerCountChange}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">변화율:</span><span className={`text-white ${tokenData.devCluster.buyerCountChangePercentage > 0 ? 'text-green-400' : tokenData.devCluster.buyerCountChangePercentage < 0 ? 'text-red-400' : ''}`}>{tokenData.devCluster.buyerCountChangePercentage > 0 ? '+' : ''}{tokenData.devCluster.buyerCountChangePercentage.toFixed(2)}%</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">시작 시간 (KST):</span><span className="text-white">{formatTimestampToLocaleString(tokenData.devCluster.startTimestamp, 'en-US', { timeZone: 'Asia/Seoul' })}</span></div>
                    <div className="flex justify-between"><span className="text-gray-400">마지막 업데이트 (KST):</span><span className="text-white">{formatTimestampToLocaleString(tokenData.devCluster.timestamp, 'en-US', { timeZone: 'Asia/Seoul' })}</span></div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-32">
              <p className="text-gray-400">토큰 데이터를 불러올 수 없습니다.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TokenDetailModal;
